import { Check, X, Eye, RefreshCcw, Download } from "lucide-react";
import { API } from "@/lib/auth";
import {
  StatusPill,
  ReviewPill,
  computeReviewState,
  formatShortDate,
  formatRelativeTime,
} from "./shared";

const VendorRow = ({
  app,
  onStatus,
  onView,
  onCycleReview,
  onMarkReviewComplete,
  onDelete,
  mode,
}) => (
  <tr
    data-testid={`vendor-row-${app.id}`}
    className="border-b border-[var(--ld-border)] hover:bg-[var(--ld-ivory-warm)]/40 transition-colors"
  >
    <td className="py-4 pr-4">
      <div className="font-serif-display text-lg text-[var(--ld-ink)]">
        {app.company_name}
      </div>
      <div className="text-xs text-[var(--ld-ink-muted)] mt-0.5">
        {app.full_name}
      </div>
    </td>
    <td className="py-4 pr-4 text-sm text-[var(--ld-ink-muted)]">
      <div className="flex flex-wrap gap-1.5">
        {(app.service_types || []).slice(0, 3).map((s) => (
          <span
            key={s}
            className="px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase border border-[var(--ld-ink)]/20"
          >
            {s}
          </span>
        ))}
        {(app.service_types || []).length > 3 && (
          <span className="text-[10px] text-[var(--ld-ink-muted)]">
            +{app.service_types.length - 3}
          </span>
        )}
      </div>
    </td>
    <td className="py-4 pr-4 text-sm text-[var(--ld-ink-muted)]">
      {(app.based_in || []).join(", ")}
    </td>
    <td className="py-4 pr-4 text-sm text-[var(--ld-ink-muted)] whitespace-nowrap">
      {formatShortDate(app.accepted_at)}
    </td>
    <td className="py-4 pr-4">
      <div className="flex flex-col items-start gap-1">
        <div className="inline-flex items-center gap-1.5">
          <ReviewPill app={app} onCycle={onCycleReview} />
          {computeReviewState(app) === "red" && (
            <button
              type="button"
              onClick={() => onMarkReviewComplete(app)}
              data-testid={`review-complete-${app.id}`}
              title="Mark review complete — resets the 10-month timer"
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.18em] uppercase border border-emerald-600/50 text-emerald-800 hover:bg-emerald-500/10 transition-colors"
            >
              <Check size={11} strokeWidth={1.8} />
              Done
            </button>
          )}
        </div>
        {app.last_reviewed_at && (
          <span
            data-testid={`review-timestamp-${app.id}`}
            className="text-[10px] text-[var(--ld-ink-muted)]"
          >
            Reviewed {formatShortDate(app.last_reviewed_at)}
          </span>
        )}
      </div>
    </td>
    <td className="py-4 pr-4">
      <span
        data-testid={`vendor-last-signin-${app.id}`}
        className={
          "text-[11px] tracking-[0.08em] " +
          (app.last_signin_at
            ? "text-[var(--ld-ink-muted)]"
            : "text-[var(--ld-ink-muted)]/55 italic")
        }
        title={
          app.last_signin_at
            ? new Date(app.last_signin_at).toLocaleString()
            : "Account has never signed in"
        }
      >
        {formatRelativeTime(app.last_signin_at)}
      </span>
    </td>
    <td className="py-4 pr-4">
      <StatusPill status={app.status} />
    </td>
    <td className="py-4 pr-2 text-right">
      <div className="inline-flex items-center gap-1.5">
        <button
          data-testid={`vendor-view-${app.id}`}
          onClick={() => onView(app)}
          title="View details"
          className="inline-flex h-8 w-8 items-center justify-center border border-[var(--ld-ink)]/30 text-[var(--ld-ink)] hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
        >
          <Eye size={13} strokeWidth={1.6} />
        </button>
        {mode === "approved" && (
          <a
            data-testid={`vendor-download-${app.id}`}
            href={`${API}/admin/vendor-applications/${app.id}/download`}
            download
            title="Download completed form + portfolio media (ZIP)"
            className="inline-flex h-8 px-3 items-center justify-center gap-1 text-[10px] tracking-[0.18em] uppercase border border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)] hover:bg-[var(--ld-gold-deep)] hover:text-white transition-colors"
          >
            <Download size={11} strokeWidth={1.8} />
            Download
          </a>
        )}
        {mode !== "rejected" && (
          <>
            <button
              data-testid={`vendor-approve-${app.id}`}
              onClick={() => onStatus(app, "approved")}
              disabled={app.status === "approved"}
              title="Approve"
              className="inline-flex h-8 px-3 items-center justify-center gap-1 text-[10px] tracking-[0.18em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] hover:bg-black hover:text-[#e9d59a] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Check size={12} strokeWidth={1.8} />
              Approve
            </button>
            <button
              data-testid={`vendor-reject-${app.id}`}
              onClick={() => onStatus(app, "rejected")}
              disabled={app.status === "rejected"}
              title="Reject"
              className="inline-flex h-8 px-3 items-center justify-center gap-1 text-[10px] tracking-[0.18em] uppercase bg-transparent text-[var(--ld-ink)] border border-[var(--ld-ink)]/40 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <X size={12} strokeWidth={1.8} />
              Reject
            </button>
          </>
        )}
        {mode === "rejected" && (
          <button
            data-testid={`vendor-restore-${app.id}`}
            onClick={() => onStatus(app, "pending")}
            title="Restore to pending"
            className="inline-flex h-8 px-3 items-center justify-center gap-1 text-[10px] tracking-[0.18em] uppercase bg-transparent text-[var(--ld-ink)] border border-[var(--ld-ink)]/40 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
          >
            <RefreshCcw size={12} strokeWidth={1.8} />
            Restore
          </button>
        )}
        {onDelete && (
          <button
            data-testid={`vendor-delete-${app.id}`}
            onClick={() => onDelete(app)}
            title="Permanently delete this application"
            className="inline-flex h-8 px-3 items-center justify-center gap-1 text-[10px] tracking-[0.18em] uppercase bg-transparent text-red-700 border border-red-700/40 hover:bg-red-700/5 transition-colors"
          >
            <X size={12} strokeWidth={1.8} />
            Delete
          </button>
        )}
      </div>
    </td>
  </tr>
);

export default VendorRow;
